import { FC, useState, useContext } from 'react';
import { Modal } from '@appkit4/react-components/modal';
import { Button } from '@appkit4/react-components/button';
import { Input } from '@appkit4/react-components/field';
import { UserContext } from '@contexts/UserProvider';
import { connectGithubPat } from '@config/getGithubApis';
import styles from './GithubConnectPatModal.module.scss';

interface GithubConnectPatModalProps {
    visible: boolean;
    onCancel: () => void;
}

const GithubConnectPatModal: FC<GithubConnectPatModalProps> = ({ visible, onCancel }) => {
    const [pat, setPat] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const { refreshGithubCredentialStatus } = useContext(UserContext);

    const handleSubmit = async () => {
        if (!pat.trim()) return;

        setIsLoading(true);
        setErrorMessage('');

        try {
            const response = await connectGithubPat(pat.trim());
            if (response && response.code === 1) {
                await refreshGithubCredentialStatus();
                setPat('');
                onCancel();
            } else {
                setErrorMessage(response?.description || 'Failed to connect GitHub token.');
            }
        } catch (error: any) {
            const msg = error?.response?.data?.description || 'Failed to connect GitHub token.';
            setErrorMessage(msg);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        setPat('');
        setErrorMessage('');
        onCancel();
    };

    return (
        <Modal
            visible={visible}
            title="Connect GitHub"
            ariaLabel="Connect GitHub"
            onCancel={handleCancel}
            modalStyle={{ width: '480px' }}
            footer={
                <>
                    <Button kind="secondary" onClick={handleCancel} disabled={isLoading}>
                        Cancel
                    </Button>
                    <Button kind="primary" onClick={handleSubmit} disabled={!pat.trim() || isLoading}>
                        {isLoading ? 'Connecting...' : 'Connect'}
                    </Button>
                </>
            }>
            <div className={styles['connect-pat-modal-body']}>
                <Input
                    type="password"
                    title="Personal access token"
                    value={pat}
                    onChange={(v: string) => {
                        setPat(v);
                        setErrorMessage('');
                    }}
                    hideTitleOnInput={false}
                />
                <p className={styles['helper-text']}>
                    Token must have <code>repo</code> scope.
                </p>
                {errorMessage && (
                    <p className={styles['error-message']}>
                        <span className="Appkit4-icon icon-alert-outline" aria-hidden="true" />
                        {errorMessage}
                    </p>
                )}
            </div>
        </Modal>
    );
};

export default GithubConnectPatModal;
